import { useEffect, useRef, useState } from 'react';
import type { DigitalMenuSettings } from 'shared-models';
import { digitalMenuService } from '../../services/digital-menu/digital-menu.service';
import { DIGITAL_MENU_UPDATE } from '../../services/digital-menu/digital-menu-management.service';
import { orderlinesService } from '../../services/orderlines/orderlines.service';
import { priceAndBuild } from '../../utils/variant-combo-pricing';
import { decodeBasketQr } from './digital-menu-basket-qr';
import { pickCounts, validChoices } from './digital-menu.utils';

export function useBasketQrImport(tenantId: string, orderId: number | null, employeeSession: string | null) {
  const [enabled, setEnabled] = useState(false);
  const [opened, setOpened] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState<{ added: number; skipped: string[] } | null>(null);
  const consumed = useRef(false);
  const lock = useRef(false);
  const generation = useRef(0);
  const readAbort = useRef<AbortController | null>(null);
  const current = useRef({ tenantId, orderId, employeeSession, enabled });
  current.current = { tenantId, orderId, employeeSession, enabled };

  useEffect(() => {
    setEnabled(false);
    if (!employeeSession) return;
    const controller = new AbortController();
    let changed = false;
    const update = (event: Event) => {
      const detail = (event as CustomEvent<{ tenantId: string; settings: DigitalMenuSettings }>).detail;
      if (detail?.tenantId === tenantId) { changed = true; setEnabled(detail.settings.isDigitalMenu); }
    };
    window.addEventListener(DIGITAL_MENU_UPDATE, update);
    digitalMenuService.profile(tenantId, controller.signal)
      .then(() => { if (!controller.signal.aborted && !changed) setEnabled(true); })
      .catch(() => { if (!controller.signal.aborted && !changed) setEnabled(false); });
    return () => { controller.abort(); window.removeEventListener(DIGITAL_MENU_UPDATE, update); };
  }, [tenantId, employeeSession]);

  useEffect(() => {
    generation.current++;
    setOpened(false); setError(''); setResult(null);
    return () => { generation.current++; readAbort.current?.abort(); };
  }, [tenantId, orderId, employeeSession, enabled]);

  const open = () => {
    if (!enabled || !orderId || !employeeSession || lock.current) return;
    consumed.current = false; setError(''); setResult(null); setOpened(true);
  };
  const scan = async (text: string) => {
    if (lock.current || consumed.current || !enabled || !orderId || !employeeSession) return false;
    consumed.current = true; lock.current = true; setBusy(true); setError('');
    const controller = new AbortController(); readAbort.current = controller;
    const started = generation.current;
    const isCurrent = () => started === generation.current && current.current.enabled
      && current.current.tenantId === tenantId && current.current.orderId === orderId
      && current.current.employeeSession === employeeSession;
    try {
      const items = decodeBasketQr(text, tenantId);
      let added = 0;
      const skipped: string[] = [];
      for (const item of items) {
        if (!isCurrent()) break;
        let label = `מוצר ${item.productId}`;
        try {
          const product = await digitalMenuService.product(tenantId, item.productId, controller.signal);
          label = product.name;
          const counts = pickCounts(item.picks);
          const { selections } = priceAndBuild(product.groups, counts, item.picks);
          if (!product.available || !validChoices(product.groups, counts)
            || selections.reduce((sum, selection) => sum + selection.selectedQuantity, 0) !== item.picks.length) {
            skipped.push(`${label}: המנה או הבחירה אינן זמינות`); continue;
          }
          if (!isCurrent()) break;
          await orderlinesService.add(tenantId, orderId, item.productId, item.quantity, employeeSession, selections);
          added += item.quantity;
        } catch {
          skipped.push(`${label}: ההוספה לא אושרה. יש לבדוק בהזמנה לפני ניסיון נוסף`);
        }
      }
      if (isCurrent()) setResult({ added, skipped });
      return isCurrent() && added > 0;
    } catch { if (isCurrent()) setError('קוד הסל אינו תקין או שייך למסעדה אחרת'); return false; }
    finally { lock.current = false; setBusy(false); }
  };
  return { enabled, opened, busy, error, result, open, scan, close: () => { if (!lock.current) setOpened(false); } };
}